'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Locale } from '../i18n/locales';
import { getSupabaseBrowserClientSafe } from '../lib/supabase/client';
import type { LocalizedRow } from '../types/content';

export type TableSubscription = {
    table: string;
    filter?: string;
    onUpdate?: (payload: Record<string, unknown>) => void;
};

/**
 * Generalized hook for real-time content updates.
 * @param initialData The server-side rendered data.
 * @param locale The current locale.
 * @param tables Array of table subscriptions or strings.
 * @param mapData Function to transform raw rows into the desired data structure.
 */
export function useRealtimeContent<T>(
    initialData: T,
    locale: Locale,
    tables: (string | TableSubscription)[],
    mapData: (prev: T, table: string, rows: LocalizedRow[]) => T
) {
    const [data, setData] = useState<T>(initialData);
    const supabase = useMemo(() => getSupabaseBrowserClientSafe(), []);

    const normalizedSubscriptions = useMemo(() =>
        tables.map(t => typeof t === 'string' ? { table: t, filter: `locale=eq.${locale}` } : { filter: `locale=eq.${locale}`, ...t }),
        [tables, locale]
    );

    const refreshTable = useCallback(async (table: string, filter?: string) => {
        if (!supabase) return;

        let query = supabase.from(table).select('*');
        if (filter) {
            const [key, val] = filter.split('=eq.');
            if (key && val) {
                query = query.eq(key, val);
            }
        }

        const { data: rows } = await query.order('order', { ascending: true });

        if (rows) {
            setData(prev => mapData(prev, table, rows as LocalizedRow[]));
        }
    }, [supabase, mapData]);

    useEffect(() => {
        if (!supabase) return;

        const channelId = `realtime-${normalizedSubscriptions.map(s => s.table).join('-')}`;
        const channel = supabase.channel(channelId);

        normalizedSubscriptions.forEach(sub => {
            channel.on(
                'postgres_changes',
                { event: '*', schema: 'public', table: sub.table, filter: sub.filter },
                (payload) => {
                    console.log(`[Realtime] Change detected in ${sub.table}:`, payload);
                    if (sub.onUpdate) {
                        sub.onUpdate(payload);
                    } else {
                        void refreshTable(sub.table, sub.filter);
                    }
                }
            );
        });

        channel.subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [supabase, normalizedSubscriptions, refreshTable]);

    return data;
}
